// FILE: src/components/Admin/AI/rag/GtmRagResolutionPanel.tsx
import React from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import EditIcon from '@mui/icons-material/Edit';

import {
  useAddRagRoutingMappingMutation,
  useFetchGtmRagResolution,
  useUpdateRagRoutingMappingMutation,
} from '../../../../hooks/rag-hooks';
import { GtmSelector, RagPipelineGroupSelector, RagPipelinePurposeSelector } from './RagSelects';

type ResolutionTab = 'overview' | 'higher-level' | 'features';

type MappingTarget = {
  scope_type: string;
  scope_id: number;
  name: string;
  current_mapping?: any | null;
  inherited_mapping?: any | null;
};

type MappingDialogProps = {
  open: boolean;
  target: MappingTarget | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => Promise<void>;
};

const scopeLabels: Record<string, string> = {
  go_to_market: 'Go-To-Market',
  solution_component: 'Solution Component',
  security_use_case: 'Security Use Case',
  security_use_case_component: 'Security Use Case Component',
  product_feature: 'Product Feature',
};

function mappingPipelineLabel(mapping: any) {
  if (!mapping) return '-';
  return mapping.pipeline_group_name || mapping.pipeline_group || mapping.pipeline_group_id || '-';
}

function mappingPipelineId(mapping: any) {
  if (!mapping) return '-';
  return mapping.active_pipeline_id || mapping.ingest_pipeline_id || '-';
}

function mappingSummary(mapping: any) {
  if (!mapping) return '-';
  return `${mappingPipelineLabel(mapping)} / active ${mapping.active_slot || '-'} / ingest ${mapping.ingest_slot || '-'}`;
}

function boolLabel(value: any) {
  return value ? 'yes' : 'no';
}

const RoutingMappingDialog: React.FC<MappingDialogProps> = ({
                                                              open,
                                                              target,
                                                              saving,
                                                              onClose,
                                                              onSubmit,
                                                            }) => {
  const [purposeId, setPurposeId] = React.useState<number | null>(null);
  const [pipelineGroupId, setPipelineGroupId] = React.useState<number | null>(null);
  const [priority, setPriority] = React.useState(100);
  const [metaFiltersText, setMetaFiltersText] = React.useState('{}');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;

    const current = target?.current_mapping || null;

    setPurposeId(current?.purpose || current?.purpose_id || null);
    setPipelineGroupId(current?.pipeline_group_id || current?.pipeline_group || null);
    setPriority(Number(current?.priority || 100));
    setMetaFiltersText(JSON.stringify(current?.meta_filters || {}, null, 2));
    setError(null);
  }, [open, target]);

  const submit = async () => {
    setError(null);

    if (!target) {
      setError('No mapping target selected.');
      return;
    }

    if (!pipelineGroupId) {
      setError('Pipeline profile is required.');
      return;
    }

    let metaFilters: Record<string, any> = {};

    try {
      metaFilters = metaFiltersText.trim() ? JSON.parse(metaFiltersText) : {};
      if (!metaFilters || typeof metaFilters !== 'object' || Array.isArray(metaFilters)) {
        throw new Error('Meta filters must be a JSON object.');
      }
    } catch (err: any) {
      setError(err?.message || 'Meta filters must be valid JSON.');
      return;
    }

    const current = target.current_mapping;

    await onSubmit({
      id: current?.mapping_source === 'rag_routing_mapping' ? current.mapping_id : undefined,
      scope_type: target.scope_type,
      scope_id: target.scope_id,
      purpose: purposeId || null,
      pipeline_group: pipelineGroupId,
      priority: Number(priority || 100),
      is_active: true,
      is_default: false,
      meta_filters: metaFilters,
      notes: '',
      updated_by: 'admin-ui',
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {target?.current_mapping?.mapping_source === 'rag_routing_mapping'
          ? 'Edit Direct RAG Mapping'
          : 'Add Direct RAG Mapping'}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {target ? (
            <Alert severity="info">
              Mapping <b>{scopeLabels[target.scope_type] || target.scope_type}</b>{' '}
              <b>{target.name}</b> directly to a RAG pipeline profile. This overrides inherited/default resolution.
            </Alert>
          ) : null}

          {target?.inherited_mapping ? (
            <Alert severity="warning">
              Current inherited mapping: <b>{mappingSummary(target.inherited_mapping)}</b>
            </Alert>
          ) : null}

          <RagPipelinePurposeSelector
            value={purposeId}
            onChange={setPurposeId}
            label="Purpose"
            helperText="Optional. Leave blank for default routing across purposes."
          />

          <RagPipelineGroupSelector
            value={pipelineGroupId}
            onChange={setPipelineGroupId}
            label="Pipeline Profile"
            required
          />

          <TextField
            fullWidth
            type="number"
            label="Priority"
            value={priority}
            onChange={(event) => setPriority(Number(event.target.value))}
          />

          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Meta Filters JSON"
            value={metaFiltersText}
            onChange={(event) => setMetaFiltersText(event.target.value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Saving...' : 'Save Mapping'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CompactMappingCard: React.FC<{ mapping: any; title: string }> = ({ mapping, title }) => (
  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
    <Typography variant="caption" color="text.secondary">
      {title}
    </Typography>

    <Typography variant="body2" fontWeight={700}>
      {mappingPipelineLabel(mapping)}
    </Typography>

    <Typography variant="body2">
      Purpose: {mapping?.purpose || '-'}
    </Typography>

    <Typography variant="caption" sx={{ wordBreak: 'break-word' }} display="block">
      Active: {mapping?.active_slot || '-'} / {mapping?.active_pipeline_id || '-'}
    </Typography>

    <Typography variant="caption" sx={{ wordBreak: 'break-word' }} display="block">
      Ingest: {mapping?.ingest_slot || '-'} / {mapping?.ingest_pipeline_id || '-'}
    </Typography>
  </Paper>
);

const GtmRagResolutionPanel: React.FC = () => {
  const [gtmId, setGtmId] = React.useState<number | null>(null);
  const [tab, setTab] = React.useState<ResolutionTab>('overview');
  const [target, setTarget] = React.useState<MappingTarget | null>(null);
  const [pageByScope, setPageByScope] = React.useState<Record<string, number>>({});
  const [featurePage, setFeaturePage] = React.useState(0);

  const query = useFetchGtmRagResolution(gtmId, {
    enabled: Boolean(gtmId),
    placeholderData: keepPreviousData,
  });

  const addMapping = useAddRagRoutingMappingMutation();
  const updateMapping = useUpdateRagRoutingMappingMutation();

  const saving = addMapping.isPending || updateMapping.isPending;

  const resolution = query.data || {};
  const relatedProducts = resolution.related_products || [];
  const unmappedProducts = resolution.unmapped_products || [];
  const higherLevelMappings = resolution.higher_level_mappings || {};
  const productFeatureMappings = resolution.product_feature_mappings || [];

  const saveMapping = async (payload: any) => {
    if (payload.id) {
      await updateMapping.mutateAsync(payload);
    } else {
      await addMapping.mutateAsync(payload);
    }

    setTarget(null);
    await query.refetch();
  };

  const openMappingDialog = (row: any, scopeType: string) => {
    setTarget({
      scope_type: scopeType,
      scope_id: row.id,
      name: row.name || `${scopeType}:${row.id}`,
      current_mapping: row.direct_mapping || (row.mapping && row.direct_mapped ? row.mapping : null),
      inherited_mapping: row.inherited_mapping || (row.inherited ? row.mapping : null),
    });
  };

  const renderMappedChip = (row: any) => (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        color={row.mapped ? 'success' : 'error'}
        variant="outlined"
        label={row.mapped ? 'mapped' : 'unmapped'}
      />
      {row.direct_mapped ? (
        <Chip size="small" color="primary" variant="outlined" label="direct" />
      ) : null}
      {row.inherited ? (
        <Chip size="small" color="warning" variant="outlined" label="inherited" />
      ) : null}
    </Stack>
  );

  const renderHigherLevelTable = (scopeType: string, rows: any[]) => {
    const page = pageByScope[scopeType] || 0;
    const rowsPerPage = 10;
    const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Paper variant="outlined" sx={{ p: 2 }} key={scopeType}>
        <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700}>
            {scopeLabels[scopeType] || scopeType}
          </Typography>
          <Chip size="small" variant="outlined" label={`${rows.length} item(s)`} />
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={`${rows.filter((row) => row.mapped).length} mapped`}
          />
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${rows.filter((row) => row.direct_mapped).length} direct`}
          />
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell width={190}>Mapping State</TableCell>
                <TableCell>Effective Mapping</TableCell>
                <TableCell width={140} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedRows.map((row: any) => (
                <TableRow key={`${scopeType}-${row.id}`} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {row.name || `${scopeType}:${row.id}`}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      ID: {row.id}
                      {row.product_id ? ` / Product: ${row.product_id}` : ''}
                      {row.use_case_id ? ` / Use Case: ${row.use_case_id}` : ''}
                    </Typography>

                    {row.description ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.description}
                      </Typography>
                    ) : null}
                  </TableCell>

                  <TableCell>{renderMappedChip(row)}</TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {mappingSummary(row.mapping)}
                    </Typography>

                    {row.inherited ? (
                      <Typography variant="caption" color="text.secondary">
                        Inherited from {row.inherited_from_scope_type || 'parent'}{' '}
                        {row.inherited_from_scope_id || ''}
                      </Typography>
                    ) : null}
                  </TableCell>

                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={row.direct_mapped ? 'outlined' : 'contained'}
                      startIcon={row.direct_mapped ? <EditIcon fontSize="small" /> : <AddLinkIcon fontSize="small" />}
                      onClick={() => openMappingDialog(row, scopeType)}
                    >
                      {row.direct_mapped ? 'Edit' : 'Map'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          onPageChange={(_, nextPage) =>
            setPageByScope((prev) => ({
              ...prev,
              [scopeType]: nextPage,
            }))
          }
          onRowsPerPageChange={() => {}}
        />
      </Paper>
    );
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          GTM RAG Resolution
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          GTM higher-level elements can map directly to a RAG profile. If not directly mapped, solution components,
          security use cases, and security use case components inherit the GTM/derived mapping. Product features inherit
          from their related product unless directly mapped.
        </Typography>

        <GtmSelector
          value={gtmId}
          onChange={id => setGtmId(id)}
          helperText="Select a GTM to inspect and manage derived/direct RAG routing."
        />
      </Paper>

      {query.isLoading || query.isFetching ? <LinearProgress /> : null}

      {query.error ? (
        <Alert severity="error">
          {(query.error as Error)?.message || 'Failed to load GTM RAG resolution.'}
        </Alert>
      ) : null}

      {!gtmId ? (
        <Alert severity="info">
          Select a GTM to view and manage RAG routing.
        </Alert>
      ) : null}

      {gtmId && query.data ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {resolution.go_to_market?.name || `GTM ${gtmId}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ID: {gtmId} / Type: {resolution.go_to_market?.type || '-'}
                </Typography>
              </Box>

              <Button
                variant={higherLevelMappings.go_to_market?.[0]?.direct_mapped ? 'outlined' : 'contained'}
                startIcon={
                  higherLevelMappings.go_to_market?.[0]?.direct_mapped
                    ? <EditIcon fontSize="small" />
                    : <AddLinkIcon fontSize="small" />
                }
                onClick={() =>
                  openMappingDialog(
                    {
                      ...(higherLevelMappings.go_to_market?.[0] || resolution.go_to_market),
                      inherited_mapping: null,
                    },
                    'go_to_market',
                  )
                }
              >
                {higherLevelMappings.go_to_market?.[0]?.direct_mapped ? 'Edit GTM Mapping' : 'Map GTM'}
              </Button>
            </Stack>
          </Paper>

          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="overview" label="Overview" />
            <Tab value="higher-level" label="GTM Elements" />
            <Tab value="features" label="Product Features" />
          </Tabs>

          {tab === 'overview' ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Selected Pipelines
                </Typography>

                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    <CompactMappingCard
                      title="Chat / Active"
                      mapping={resolution.selected_chat_pipeline}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <CompactMappingCard
                      title="Entity Documentation / Ingest"
                      mapping={resolution.selected_ingest_pipeline}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Related Cisco Products
                  </Typography>
                  <Chip size="small" variant="outlined" label={`${relatedProducts.length} products`} />
                  <Chip size="small" variant="outlined" label={`${unmappedProducts.length} unmapped`} />
                </Stack>

                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 1000 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell width={120}>Mapped</TableCell>
                        <TableCell>Mappings</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {relatedProducts.map((product: any) => (
                        <TableRow key={product.id || product.product_id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {product.name || product.product_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {product.id || product.product_id} / Vendor: {product.vendor || '-'} / Offer:{' '}
                              {product.offer_type || '-'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              color={product.mapped ? 'success' : 'error'}
                              variant="outlined"
                              label={product.mapped ? 'mapped' : 'unmapped'}
                            />
                          </TableCell>

                          <TableCell>
                            <Stack spacing={1}>
                              {(product.mappings || []).map((mapping: any, idx: number) => (
                                <Paper key={`${product.id || product.product_id}-${idx}`} variant="outlined" sx={{ p: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Purpose: {mapping.purpose || '-'}
                                  </Typography>
                                  <Typography variant="body2">
                                    Group: <b>{mapping.pipeline_group_name || mapping.pipeline_group || '-'}</b>
                                  </Typography>
                                  <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                                    Active: {mapping.active_slot || '-'} / {mapping.active_pipeline_id || '-'}
                                  </Typography>
                                  <Typography variant="caption" display="block" sx={{ wordBreak: 'break-word' }}>
                                    Ingest: {mapping.ingest_slot || '-'} / {mapping.ingest_pipeline_id || '-'}
                                  </Typography>
                                </Paper>
                              ))}

                              {!product.mappings?.length ? (
                                <Typography variant="body2" color="text.secondary">
                                  No active product RAG mapping found.
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                {!relatedProducts.length ? (
                  <Typography variant="body2" color="text.secondary" mt={2}>
                    No related Cisco products were returned for this GTM.
                  </Typography>
                ) : null}
              </Paper>
            </Stack>
          ) : null}

          {tab === 'higher-level' ? (
            <Stack spacing={2}>
              {renderHigherLevelTable('go_to_market', higherLevelMappings.go_to_market || [])}
              {renderHigherLevelTable('solution_component', higherLevelMappings.solution_component || [])}
              {renderHigherLevelTable('security_use_case', higherLevelMappings.security_use_case || [])}
              {renderHigherLevelTable(
                'security_use_case_component',
                higherLevelMappings.security_use_case_component || [],
              )}
            </Stack>
          ) : null}

          {tab === 'features' ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" fontWeight={700}>
                  Underlying Product Features
                </Typography>
                <Chip size="small" variant="outlined" label={`${productFeatureMappings.length} feature references`} />
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`${productFeatureMappings.filter((row: any) => row.mapped).length} mapped`}
                />
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${productFeatureMappings.filter((row: any) => row.direct_mapped).length} direct`}
                />
              </Stack>

              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Feature</TableCell>
                      <TableCell width={190}>Mapping State</TableCell>
                      <TableCell>Effective Mapping</TableCell>
                      <TableCell width={180}>Source</TableCell>
                      <TableCell width={140} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {productFeatureMappings
                      .slice(featurePage * 10, featurePage * 10 + 10)
                      .map((row: any, idx: number) => (
                        <TableRow key={`${row.id}-${row.source_scope}-${row.source_scope_id}-${idx}`} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {row.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Feature ID: {row.id} / Product: {row.product_name || row.product_id}
                            </Typography>
                          </TableCell>

                          <TableCell>{renderMappedChip(row)}</TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {mappingSummary(row.mapping)}
                            </Typography>

                            {row.inherited ? (
                              <Typography variant="caption" color="text.secondary">
                                Inherited from product {row.inherited_from_scope_id}
                              </Typography>
                            ) : null}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {scopeLabels[row.source_scope] || row.source_scope}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.source_scope_name || row.source_scope_id}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            <Button
                              size="small"
                              variant={row.direct_mapped ? 'outlined' : 'contained'}
                              startIcon={
                                row.direct_mapped
                                  ? <EditIcon fontSize="small" />
                                  : <AddLinkIcon fontSize="small" />
                              }
                              onClick={() => openMappingDialog(row, 'product_feature')}
                            >
                              {row.direct_mapped ? 'Edit' : 'Map'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>

              <TablePagination
                component="div"
                count={productFeatureMappings.length}
                page={featurePage}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
                onPageChange={(_, nextPage) => setFeaturePage(nextPage)}
                onRowsPerPageChange={() => {}}
              />
            </Paper>
          ) : null}
        </Stack>
      ) : null}

      <RoutingMappingDialog
        open={!!target}
        target={target}
        saving={saving}
        onClose={() => setTarget(null)}
        onSubmit={saveMapping}
      />
    </Stack>
  );
};

export default GtmRagResolutionPanel;